import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { vendorUsersQueryKey } from "~/shared/hooks/use-vendor-users.ts";
import { vendorUserMutationResponseSchema } from "~/shared/schemas/vendor-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

import { extractVendorUserError } from "./extract-vendor-user-error.ts";

type UpdateVendorUserVariables = {
	userId: string;
	firstName: string | null;
	lastName: string | null;
	customerId: number;
};

export const useUpdateVendorUser = () => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();
	const { isInternal } = useDeploymentType();

	const { mutateAsync: updateVendorUser, isPending } = useMutation({
		mutationFn: async ({ customerId, ...user }: UpdateVendorUserVariables) => {
			const route = isInternal
				? apiRoutes.INTERNALUPDATEVENDOR.replace(
						":customerId",
						String(customerId),
					)
				: apiRoutes.UPDATEVENDOR;
			const resp = await postAndValidate(
				route,
				user,
				vendorUserMutationResponseSchema,
				{ onError: extractVendorUserError },
			);
			if (!resp.success) {
				throw new Error(resp.error);
			}
			return resp.data;
		},
		onSuccess: () => {
			showSuccess("Vendor user updated successfully");
			// Fire-and-forget so the modal closes immediately; the grid's
			// isFetching state drives the skeleton loaders during refetch.
			void queryClient.invalidateQueries({ queryKey: vendorUsersQueryKey });
		},
		onError: (error: unknown) => {
			showError(
				error instanceof Error && error.message
					? error.message
					: "Failed to update vendor user",
			);
		},
	});

	return { updateVendorUser, isPending };
};
